import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubmissionModal = ({ isOpen, onClose, onLogin, onRegister }) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('conferences');
  
  if (!isOpen) return null;

  /* Remember which conference the person was trying to submit to, so we can
     put them back there once they have an account or a session.
     `/user-sign-in` already reads this key and redirects to the submission
     portal — but only the REGISTER button used to write it, so signing in
     dropped you on the dashboard with no idea what you had been doing. Both
     paths lead to the same place, so both record the same intent. */
  const rememberConference = () => {
    // Accepts both spellings. The app used ?id= on six routes and
    // ?conferenceId= on three, so links between them only worked if you knew
    // which one each page wanted. Everything now *emits* ?conferenceId=;
    // reading ?id= as well keeps already-shared and bookmarked links alive.
    const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
    if (conferenceId) {
      sessionStorage?.setItem('pendingSubmissionConferenceId', conferenceId);
    }
  };

  const handleLogin = () => { rememberConference(); onLogin?.(); };
  const handleRegister = () => { rememberConference(); onRegister?.(); };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-5 w-full max-w-md p-6 md:p-8 animate-in fade-in zoom-in duration-250">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-all duration-250"
          aria-label={t('actions.close', { ns: 'common', defaultValue: 'Close' })}
        >
          <Icon name="X" size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={32} className="text-primary" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            {t('submissionModal.title', 'Sign in to continue')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('submissionModal.body', 'Submitting a paper needs an account, so we can track it and keep you posted.')}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleLogin}
            iconName="LogIn"
            iconPosition="left"
          >
            {t('submissionModal.signIn', 'Sign in to submit')}
          </Button>

          {/* Outline, not a second solid fill: two identical primary buttons
              give equal weight to two different answers and read as a choice
              you cannot get wrong — which this one you can. */}
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={handleRegister}
            iconName="UserPlus"
            iconPosition="left"
          >
            {t('submissionModal.register', 'Create an account')}
          </Button>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground caption">
              {t('submissionModal.note', 'An account lets you track your submissions, receive notifications, and manage papers for several conferences from one dashboard.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModal;